import { RegistrationSection } from '../builders/registration_section_builder';
import { ScheduledSectionsQuery } from '../queries/scheduled_sections_query';
import { RegistrationAlertsServant } from './registration_alerts_servant';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ISectionAvailabilityDatasource } from '../../sections/datasources/section_availability_datasource';
import { IWaitlistStatusDatasource } from '../datasources/waitlist_status_datasource';
import { RegistrationWaitlistPositionModel } from '../models/registration_waitlist_position_model';
import { RequisiteCourseServant } from './requisite_course_servant';
import { ScheduledSectionServant } from './scheduled_section_servant';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { TermScheduleFlags } from '../services/term_schedule_flags';
import { ITermCoursesDatasource } from '../../term_courses/datasources/term_courses_datasource';
import { IRegisterSectionsDatasource } from '../datasources/register_sections_datasource';
import { ScheduledSectionStatusServant } from './scheduled_section_status_servant';
import {
  ISection,
  IRegistrationSection,
  IPusherOutcome,
  IRegistrationWaitlistPusherOutcome
} from '../../../interfaces/scheduler_interfaces';
import {
  RegistrationWaitlistOutcomeStatusStrategy,
  RegistrationWaitlistOutcomeStatusConditionalStrategy,
  IRegistrationWaitlistOutcomeStatusContext,
  OUTCOME_STATUS_TYPES
} from '../strategies/registration_waitlist_outcome_status_strategy';

import { IDefineCommand, ICommand, IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { UpdateSectionSelectionCommand } from '../../../components/section_selections/commands/update_section_selection_command';

export class WaitlistCallbackServant {
  constructor(
    private ngRegisterSectionsDatasource: IRegisterSectionsDatasource,
    private ngScheduledSectionsQuery: ScheduledSectionsQuery,
    private ngSectionsDatasource: ISectionsDatasource,
    private ngRequisiteCourseServant: RequisiteCourseServant,
    private ngSectionAvailabilityDatasource: ISectionAvailabilityDatasource,
    private $stateParams: ng.ui.IStateParamsService,
    private $state: ng.ui.IStateService,
    private ngWaitlistStatusDatasource: IWaitlistStatusDatasource,
    private ngRegistrationAlertsServant: RegistrationAlertsServant,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngRegistrationWaitlistPositionModel: RegistrationWaitlistPositionModel,
    private unscheduleSectionCommand: ICommand,
    private ngTermScheduleFlags: TermScheduleFlags,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant,
    private getSectionAvailabilityCommand: ICommand,
    private updateSectionSelectionsCommand: UpdateSectionSelectionCommand
  ) {}

  isWaitlistRequestResultByType(pusherOutc: any, outcomeTypeToFilterBy: OUTCOME_STATUS_TYPES): boolean {
    const outcomeType = RegistrationWaitlistOutcomeStatusStrategy.getByKey(
      RegistrationWaitlistOutcomeStatusConditionalStrategy.evaluate(pusherOutc)
    );
    return _4.isEqual(outcomeTypeToFilterBy, outcomeType.name);
  }

  waitlistSuccess(outcomeData: IRegistrationWaitlistPusherOutcome): void {
    const sectionId = outcomeData.id;
    const sectionToWaitlist = _4.find(
      [...this.ngScheduledSectionsQuery.result.list, ..._4.toArray(this.ngSectionsDatasource.read())],
      (section: any) => section.id === sectionId
    );
    const isAutoWaitlistStatus = this.ngScheduledSectionServant.isAutoWailistSection(sectionToWaitlist);
    const isSectionWithStatus = this.ngScheduledSectionStatusServant.hasStatus(sectionToWaitlist);

    this.ngRegistrationAlertsServant.hidePendingAlert();

    const sectionToScheduleAvailability = this.ngSectionAvailabilityDatasource.get(sectionId);
    if (sectionToScheduleAvailability) {
      const {
        id,
        is_seat_available,
        is_waitlist_allowed,
        is_waitlist_full,
        is_auto_waitlist,
        avail_seats,
        last_refresh_ts,
        linked_section_coreqs_satisfied,
        has_linked_section_coreqs
      } = sectionToScheduleAvailability;
      this.ngWaitlistStatusDatasource.put({
        id,
        is_seat_available,
        is_waitlist_allowed,
        is_waitlist_full,
        is_auto_waitlist,
        avail_seats,
        last_refresh_ts,
        linked_section_coreqs_satisfied,
        has_linked_section_coreqs
      });
    }
    // TODO: recheck this condition, what are the scenarios for this?
    if (sectionToWaitlist) {
      sectionToWaitlist.status = ScheduledSectionStatus.WAITLISTED;
      this.ngSectionsDatasource.put(sectionToWaitlist);
    }

    const regSection = this.ngSectionsDatasource.metasource().compose(sectionToWaitlist);
    const isSelected = ScheduledSectionServant.canBeSelected(regSection, this.ngTermScheduleFlags);
    regSection.is_selected = isSelected;
    this.ngRegisterSectionsDatasource.put(regSection as RegistrationSection);
    this.updateSectionSelectionsCommand(regSection.id, isSelected);

    this.ngRegistrationWaitlistPositionModel.put({
      place_in_waitlist: _4.get(outcomeData, 'place_in_waitlist', 0),
      waitlist_position: _4.get(outcomeData, 'waitlist_position', 0),
      section: outcomeData.id
    });

    this.ngRegistrationAlertsServant.showWaitlistAlert(sectionToWaitlist, 'success', '');

    // show co-pre requisites growler
    const matchingFullSection = _4.find(this.ngRegisterSectionsDatasource.values(), (section: IRegistrationSection) =>
      _4.isEqual(section.id, sectionId)
    );
    const courseRequisitesForAlert = this.ngRequisiteCourseServant.getRequisitesForSection(
      sectionId,
      matchingFullSection._course
    );
    if (!_4.isEmpty([courseRequisitesForAlert])) {
      this.ngRegistrationAlertsServant.showHasRequisitesWaitlistAlert(courseRequisitesForAlert);
    }

    // TODO: verify if this should be executed after running query mediator
    if (sectionToWaitlist) {
      this.ngRegistrationAlertsServant.showOtherRequiredSectionTypesAlert(sectionToWaitlist);
    }
  }

  waitlistFails(outcomeData: IRegistrationWaitlistPusherOutcome): void {
    const sectionId = outcomeData.id;
    const sectionToWaitlist = _4.find(
      [...this.ngScheduledSectionsQuery.result.list, ..._4.toArray(this.ngSectionsDatasource.read())],
      (section: any) => section.id === sectionId
    );
    const isAutoWaitlistStatus = this.ngScheduledSectionServant.isAutoWailistSection(sectionToWaitlist);
    const isSectionWithStatus = this.ngScheduledSectionStatusServant.hasStatus(sectionToWaitlist);

    this.ngRegistrationAlertsServant.hidePendingAlert();
    // waitlist fails AND it's autowaitlist: REGISTER
    if (isAutoWaitlistStatus) {
      this.ngRegistrationAlertsServant.showAutoWaitlistSectionRegisteredAlerts(outcomeData[sectionToWaitlist.id]);
      if (sectionToWaitlist) {
        sectionToWaitlist.status = ScheduledSectionStatus.REGISTERED;
        this.ngSectionsDatasource.put(sectionToWaitlist);
      }
    } else {
      // if waitlist fails and if course is full. Also unschedule it.
      if (sectionToWaitlist) {
        sectionToWaitlist.status = ScheduledSectionStatus.UNSCHEDULED;
        this.ngSectionsDatasource.put(sectionToWaitlist);
      }
      this.ngRegistrationAlertsServant.showWaitlistAlert(sectionToWaitlist, 'error', outcomeData.message);
      if (isSectionWithStatus) {
        this.unscheduleSectionCommand(this.$stateParams.termId, sectionId).then(() => {
          if (this.$state.is('my.schedule.registration.courseSections')) {
            this.getSectionAvailabilityCommand(
              this.$stateParams.termId,
              this.$stateParams.courseId,
              this.$stateParams.sectionTypeId || 0
            );
          }
        });
      }
    }

    // TODO: verify if this should be executed after running query mediator
    if (sectionToWaitlist) {
      this.ngRegistrationAlertsServant.showOtherRequiredSectionTypesAlert(sectionToWaitlist);
    }
  }
}

angular.module('capability-ng-scheduler').service('ngWaitlistCallbackServant', WaitlistCallbackServant);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/waitlist_callback_servant.ts